import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, ShoppingBag, Euro, Clock, Package,
  Download, Printer, Calendar, Users,
  BarChart3, Target, ArrowUp, DollarSign, ChefHat, Receipt,
  Filter, X, ChevronDown, MapPin,
  PieChart, Megaphone, UserCircle, Truck, Store
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import printService from "@/services/PrintService";
import { configService } from "@/services/ConfigService";
import { useAuth } from "@/context/AuthContext";
import API from "@/utils/api";
const PRINTER_SERVICE = "http://localhost:8001";

const statusLabels = {
  pending: "Pendiente",
  preparing: "Preparando",
  ready: "Listo",
  on_the_way: "En camino",
  delivered: "Entregado",
  cancelled: "Cancelado"
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [crmStats, setCrmStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hourlyData, setHourlyData] = useState([]);
  const [dateFilter, setDateFilter] = useState("today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredStats, setFilteredStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const isDemo = sessionStorage.getItem("demo_mode") === "true";
    if (isDemo) {
      navigate("/admin/pos", { replace: true });
      return;
    }

    const checkDeliveryOnly = async () => {
      const currentConfig = await configService.loadConfig();
      if (currentConfig?.delivery?.delivery_only_mode === true) {
        navigate("/admin/delivery", { replace: true });
      }
    };
    checkDeliveryOnly();
  }, [navigate]);

  // Load branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API}/admin/branches`, { withCredentials: true });
        const activeBranches = res.data.filter(b => b.active);
        setBranches(activeBranches);

        // Pre-select based on user profile and lock if only 1 branch
        if (user?.assigned_branches?.length > 0) {
          setSelectedBranch(user.assigned_branches[0]);
        }
      } catch (error) {
        console.error("Error loading branches");
      }
    };
    fetchBranches();
  }, [user]);

  // Determine if branch selector should be shown
  const showBranchSelector = (user?.role === "admin" && (!user?.assigned_branches || user?.assigned_branches.length !== 1)) || (user?.assigned_branches?.length > 1);

  // Calculate date ranges
  const getDateRange = (filter) => {
    const now = new Date();
    // Reset time parts to ensure full day coverage
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (filter) {
      case "today":
        return { start: todayStart, end: todayEnd };
      case "yesterday":
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(todayStart.getDate() - 1);
        const yesterdayEnd = new Date(yesterdayStart);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { start: yesterdayStart, end: yesterdayEnd };
      case "week":
        // Start of current week (Monday)
        const day = todayStart.getDay() || 7; // Get current day number, converting Sun (0) to 7
        if (day !== 1) todayStart.setHours(-24 * (day - 1)); // Adjust backwards to Monday
        return { start: todayStart, end: todayEnd };
      case "month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        return { start: monthStart, end: todayEnd };
      case "custom":
        if (startDate && endDate) {
          // Adjust custom dates to full start and end of days
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          return { start, end };
        }
        return { start: todayStart, end: todayEnd };
      default:
        return { start: todayStart, end: todayEnd };
    }
  };

  const filterOrdersByDate = (orders, filter) => {
    const { start, end } = getDateRange(filter);

    const filtered = orders.filter(order => {
      const orderDate = order.created_at ? new Date(order.created_at) : null;
      if (!orderDate) return false;
      return orderDate >= start && orderDate <= end;
    });

    setFilteredOrders(filtered);
    setRecentOrders(filtered.slice(0, 8));

    // Calculate filtered stats
    const totalSales = filtered.reduce((sum, order) =>
      order.status !== 'cancelled' ? sum + (order.total || 0) : sum, 0
    );
    const validOrders = filtered.filter(o => o.status !== 'cancelled');
    const pendingOrders = filtered.filter(o => ['pending', 'preparing'].includes(o.status));

    const externalOrders = validOrders.filter(o => ['uber', 'glovo', 'justeat'].includes(o.source));
    const hubOrders = validOrders.filter(o => o.dispatched_to_hub === true);

    setFilteredStats({
      orders_count: validOrders.length,
      sales_total: totalSales,
      pending_count: pendingOrders.length,
      avg_ticket: validOrders.length > 0 ? totalSales / validOrders.length : 0,
      external_count: externalOrders.length,
      hub_count: hubOrders.length
    });

    generateHourlyData(filtered);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = {};
        if (selectedBranch && selectedBranch !== 'all') {
          params.branch_id = selectedBranch;
        }

        const [statsRes, ordersRes, crmRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { params, withCredentials: true }),
          axios.get(`${API}/admin/orders`, { params, withCredentials: true }),
          axios.get(`${API}/admin/analytics/dashboard`, { params, withCredentials: true }),
        ]);

        setAllOrders(ordersRes.data);
        setStats(statsRes.data);
        setCrmStats(crmRes.data);
        filterOrdersByDate(ordersRes.data, dateFilter);
      } catch (error) {
        console.error("Error al cargar datos:", error);
        toast.error("Error al cargar datos del dashboard");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [selectedBranch]);

  useEffect(() => {
    if (allOrders.length > 0) {
      filterOrdersByDate(allOrders, dateFilter);
    }
  }, [dateFilter, startDate, endDate]);

  const generateHourlyData = (orders) => {
    const hourly = Array(24).fill(0);

    orders.forEach(order => {
      const orderDate = order.created_at ? new Date(order.created_at) : null;
      if (orderDate && order.status !== 'cancelled') {
        const hour = orderDate.getHours();
        hourly[hour] += order.total || 0;
      }
    });

    setHourlyData(hourly.map((total, hour) => ({ hour, total })));
  };

  const printReport = () => {
    const filterLabels = {
      today: "Hoy",
      yesterday: "Ayer",
      week: "Esta Semana",
      month: "Este Mes",
      custom: "Personalizado"
    };

    const reportDate = new Date().toLocaleDateString('es-ES', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Reporte ${filterLabels[dateFilter]} - ${reportDate}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 3px solid #000;
              padding-bottom: 20px;
            }
            .header h1 {
              font-size: 28px;
              color: #000;
              margin-bottom: 5px;
            }
            .header p {
              color: #666;
              font-size: 14px;
            }
            .filter-info {
              text-align: center;
              background-color: #f5f5f5;
              padding: 10px;
              margin-bottom: 20px;
              border-radius: 5px;
              font-weight: bold;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 20px;
              margin-bottom: 30px;
            }
            .stat-card {
              border: 2px solid #e0e0e0;
              padding: 20px;
              border-radius: 8px;
            }
            .stat-label {
              font-size: 12px;
              color: #666;
              text-transform: uppercase;
              margin-bottom: 5px;
            }
            .stat-value {
              font-size: 32px;
              font-weight: bold;
              color: #000;
            }
            .orders-section {
              margin-top: 30px;
              page-break-inside: avoid;
            }
            .orders-section h2 {
              font-size: 20px;
              margin-bottom: 15px;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
            }
            th, td {
              padding: 12px;
              text-align: left;
              border-bottom: 1px solid #e0e0e0;
            }
            th {
              background-color: #f5f5f5;
              font-weight: bold;
              font-size: 14px;
            }
            td {
              font-size: 13px;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-size: 12px;
              color: #999;
              border-top: 1px solid #e0e0e0;
              padding-top: 20px;
            }
            @media print {
              body { padding: 20px; }
              .page-break { page-break-before: always; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🌮 Conexo POS - Reporte de Ventas</h1>
            <p>${reportDate}</p>
          </div>

          <div class="filter-info">
            📅 Periodo: ${filterLabels[dateFilter]}
            ${dateFilter === 'custom' && startDate && endDate ?
        ` (${new Date(startDate).toLocaleDateString('es-ES')} - ${new Date(endDate).toLocaleDateString('es-ES')})`
        : ''}
          </div>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-label">📦 Total de Pedidos</div>
              <div class="stat-value">${filteredStats?.orders_count || 0}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">💰 Ventas Totales</div>
              <div class="stat-value">${filteredStats?.sales_total?.toFixed(2) || "0.00"} €</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">📊 Ticket Promedio</div>
              <div class="stat-value">${filteredStats?.avg_ticket?.toFixed(2) || "0.00"} €</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">⏳ Pendientes</div>
              <div class="stat-value">${filteredStats?.pending_count || 0}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">💵 Ventas Mes (Total)</div>
              <div class="stat-value">${stats?.sales_month?.toFixed(2) || "0.00"} €</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">🎯 Pedidos Mes (Total)</div>
              <div class="stat-value">${stats?.orders_month || 0}</div>
            </div>
          </div>

          <div class="orders-section">
            <h2>Pedidos del Periodo</h2>
            <table>
              <thead>
                <tr>
                  <th>ID Pedido</th>
                  <th>Cliente</th>
                  <th>Estado</th>
                  <th>Total</th>
                  <th>Fecha y Hora</th>
                </tr>
              </thead>
              <tbody>
                ${filteredOrders.map(order => `
                  <tr>
                    <td>#${order.order_id?.slice(-6) || 'N/A'}</td>
                    <td>${order.user_name || 'Cliente'}</td>
                    <td>${statusLabels[order.status] || order.status || 'N/A'}</td>
                    <td>${order.total?.toFixed(2) || '0.00'} €</td>
                    <td>${order.created_at ? new Date(order.created_at).toLocaleString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        }) : 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          ${stats?.best_sellers && stats.best_sellers.length > 0 ? `
            <div class="orders-section page-break">
              <h2>Productos Más Vendidos (General)</h2>
              <table>
                <thead>
                  <tr>
                    <th>Posición</th>
                    <th>Producto</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  ${stats.best_sellers.slice(0, 15).map((item, index) => `
                    <tr>
                      <td>${index + 1}</td>
                      <td>${item._id}</td>
                      <td>${item.count} uds</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div class="footer">
            <p>Generado el ${new Date().toLocaleString('es-ES')}</p>
            <p>Sistema de Gestión para Restaurantes - 2026</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    toast.success("Reporte generado para imprimir");
  };

  const printThermalReport = async () => {
    if (!filteredStats) return;

    const filterLabels = {
      today: "HOY",
      yesterday: "AYER",
      week: "SEMANA",
      month: "MES",
      custom: "PERSONALIZADO"
    };

    let t = "\n";
    t += "         Conexo POS\n";
    t += "     DASHBOARD RESUMEN\n";
    t += "   " + new Date().toLocaleString('es-ES') + "\n";
    t += "--------------------------------\n\n";

    t += "PERIODO: " + filterLabels[dateFilter] + "\n\n";
    t += "PEDIDOS:   " + (filteredStats.orders_count || 0) + "\n";
    t += "VENTAS:    " + (filteredStats.sales_total || 0).toFixed(2) + " EUR\n";
    t += "PENDIENTE: " + (filteredStats.pending_count || 0) + "\n";
    t += "PROMEDIO:  " + (filteredStats.avg_ticket || 0).toFixed(2) + " EUR\n";
    t += "--------------------------------\n";
    t += "\n\n\n\n";

    const success = await printService.printText(t);
    if (success) {
      toast.success("Reporte enviado a tickera");
    } else {
      toast.error("Error al imprimir directo con QZ Tray");
    }
  };

  const downloadCSV = () => {
    const filterLabels = {
      today: "hoy",
      yesterday: "ayer",
      week: "semana",
      month: "mes",
      custom: "personalizado"
    };

    const csv = [
      ['ID Pedido', 'Cliente', 'Estado', 'Total', 'Fecha'],
      ...filteredOrders.map(order => [
        order.order_id?.slice(-6) || 'N/A',
        order.user_name || 'Cliente',
        statusLabels[order.status] || order.status || 'N/A',
        order.total?.toFixed(2) || '0.00',
        order.created_at ? new Date(order.created_at).toLocaleString('es-ES') : 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(["\ufeff" + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `pedidos_${filterLabels[dateFilter]}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("Archivo CSV descargado");
  };

  if (isLoading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-32 bg-gray-900 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  const peakHour = hourlyData.reduce((max, curr) => curr.total > max.total ? curr : max, { hour: 0, total: 0 });

  const filterOptions = [
    { value: "today", label: "Hoy" },
    { value: "yesterday", label: "Ayer" },
    { value: "week", label: "Esta Semana" },
    { value: "month", label: "Este Mes" },
    { value: "custom", label: "Personalizado" }
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Header with Actions and Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-yellow-500">Dashboard</h1>
              <p className="text-gray-400 mt-1">Resumen completo de Conexo POS</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {showBranchSelector && (
                <Select value={selectedBranch || "all"} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <SelectValue placeholder="Todas las sucursales" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Button
                onClick={downloadCSV}
                variant="outline"
                size="sm"
                className="border-gray-700 text-gray-300 hover:text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button
                onClick={printThermalReport}
                variant="outline"
                size="sm"
                className="border-gray-700 text-gray-300 hover:text-white"
              >
                <Receipt className="w-4 h-4 mr-2" />
                Tickera
              </Button>
              <Button
                onClick={printReport}
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
              >
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </Button>
            </div>
          </div>

          {/* Date Filter */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center gap-2 text-gray-300">
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filtrar por periodo:</span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {filterOptions.map(option => (
                    <Button
                      key={option.value}
                      size="sm"
                      variant={dateFilter === option.value ? "default" : "outline"}
                      className={dateFilter === option.value
                        ? "bg-yellow-500 text-black hover:bg-yellow-400"
                        : "border-gray-700 text-gray-300 hover:text-white"}
                      onClick={() => setDateFilter(option.value)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>

                {dateFilter === "custom" && (
                  <div className="flex items-center gap-2 ml-auto">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white w-36"
                    />
                    <span className="text-gray-400">-</span>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white w-36"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Pedidos
              </CardTitle>
              <ShoppingBag className="w-5 h-5 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.orders_count || 0}</div>
              <p className="text-xs text-gray-400 mt-1">
                En el periodo seleccionado
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Ventas
              </CardTitle>
              <Euro className="w-5 h-5 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.sales_total?.toFixed(2) || "0.00"} €</div>
              <p className="text-xs text-gray-400 mt-1">
                Volumen de facturación
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border-purple-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Integraciones
              </CardTitle>
              <Store className="w-5 h-5 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.external_count || 0}</div>
              <p className="text-xs text-gray-400 mt-1">
                Uber, Glovo, Just Eat
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Delivery Hub
              </CardTitle>
              <Truck className="w-5 h-5 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.hub_count || 0}</div>
              <p className="text-xs text-gray-400 mt-1">
                Pedidos despachados a la red
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Ticket promedio
              </CardTitle>
              <DollarSign className="w-5 h-5 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.avg_ticket?.toFixed(2) || "0.00"} €</div>
              <p className="text-xs text-gray-400 mt-1">
                Por pedido
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">
                Pendientes
              </CardTitle>
              <Clock className="w-5 h-5 text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{filteredStats?.pending_count || 0}</div>
              <p className="text-xs text-gray-400 mt-1">
                En preparación
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Ventas mes (Total)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats?.sales_month?.toFixed(2) || "0.00"} €</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Pedidos mes (Total)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{stats?.orders_month || 0}</div>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Hora pico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{peakHour.hour}:00</div>
              <p className="text-xs text-gray-400">{peakHour.total.toFixed(2)} € vendidos</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-900 border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Clientes Totales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{crmStats?.customers?.total || 0}</div>
              <p className="text-xs text-gray-400">{crmStats?.customers?.vip || 0} VIPs</p>
            </CardContent>
          </Card>
        </div>

        {/* CRM and Marketing Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Customer Segments */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <PieChart className="w-4 h-4 text-primary" />
                Segmentación CRM
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">Nuevos</span>
                <span className="text-sm font-bold text-white">{crmStats?.customers?.new || 0}</span>
              </div>
              <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-yellow-500 h-full"
                  style={{ width: `${(crmStats?.customers?.new / (crmStats?.customers?.total || 1)) * 100}%` }}
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">Recurrentes</span>
                <span className="text-sm font-bold text-white">{crmStats?.customers?.repeat || 0}</span>
              </div>
              <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-green-500 h-full"
                  style={{ width: `${(crmStats?.customers?.repeat / (crmStats?.customers?.total || 1)) * 100}%` }}
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">Inactivos</span>
                <span className="text-sm font-bold text-white">{crmStats?.customers?.inactive || 0}</span>
              </div>
              <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-red-500 h-full"
                  style={{ width: `${(crmStats?.customers?.inactive / (crmStats?.customers?.total || 1)) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Marketing Conversion */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-orange-500" />
                Marketing & Cupones
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center pt-4">
              <div className="text-4xl font-black text-white">{crmStats?.marketing?.conversion_rate || 0}%</div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Tasa de Conversión</p>
              <div className="mt-6 flex justify-around w-full border-t border-gray-800 pt-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-white">{crmStats?.marketing?.coupons_generated || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase">Emitidos</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white">{crmStats?.marketing?.coupons_used || 0}</div>
                  <div className="text-[10px] text-gray-500 uppercase">Usados</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Products Recap */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                Top 3 Productos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {crmStats?.top_products?.slice(0, 3).map((p, i) => (
                <div key={i} className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-black text-gray-600">{i + 1}</span>
                    <span className="text-xs font-medium text-gray-300 group-hover:text-primary transition-colors">{p.name}</span>
                  </div>
                  <span className="text-xs font-black text-white">{p.quantity} uds</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Hourly Sales Chart */}
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-yellow-500" />
              Ventas por hora (Periodo seleccionado)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end gap-1">
              {hourlyData.map((data, index) => {
                const maxValue = Math.max(...hourlyData.map(d => d.total), 1);
                const height = (data.total / maxValue) * 100;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center group">
                    <div
                      className="w-full bg-gradient-to-t from-yellow-500 to-orange-500 rounded-t transition-all hover:opacity-80 relative"
                      style={{ height: `${height}% `, minHeight: data.total > 0 ? '4px' : '0' }}
                    >
                      {data.total > 0 && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                          {data.total.toFixed(2)}€
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-500 mt-1">{String(index).padStart(2, '0')}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Pedidos del periodo</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredOrders.length === 0 ? (
                <p className="text-gray-400 text-sm">No hay pedidos en este periodo</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {recentOrders.map((order) => {
                    const statusColors = {
                      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                      preparing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                      ready: 'bg-green-500/20 text-green-400 border-green-500/30',
                      on_the_way: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                      delivered: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
                      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
                    };

                    return (
                      <div
                        key={order.order_id}
                        className="flex items-center justify-between py-3 px-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-bold text-white">#{order.order_id?.slice(-6) || 'N/A'}</p>
                          <p className="text-sm text-gray-400">{order.user_name || 'Cliente'}</p>
                          <p className="text-xs text-gray-500">
                            {order.created_at ? new Date(order.created_at).toLocaleString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right flex items-center gap-3">
                          <div>
                            <p className="font-bold text-yellow-500">{order.total?.toFixed(2) || '0.00'} €</p>
                            <Badge className={`text - xs ${statusColors[order.status] || statusColors.pending} border`}>
                              {statusLabels[order.status] || order.status || 'pendiente'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Best Sellers */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-yellow-500" />
                Productos más vendidos (General)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.best_sellers || stats.best_sellers.length === 0 ? (
                <p className="text-gray-400 text-sm">No hay datos disponibles</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {stats.best_sellers.slice(0, 10).map((item, index) => {
                    const medals = ['🥇', '🥈', '🥉'];
                    return (
                      <div
                        key={item._id}
                        className="flex items-center justify-between py-2 px-3 bg-gray-800/50 rounded-lg border border-gray-700"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="text-2xl">
                            {medals[index] || `${index + 1} º`}
                          </span>
                          <div>
                            <p className="font-medium text-white">{item._id}</p>
                            <p className="text-xs text-gray-400">{item.count} unidades vendidas</p>
                          </div>
                        </div>
                        <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-yellow-500 to-orange-500"
                            style={{ width: `${(item.count / stats.best_sellers[0].count) * 100}% ` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
